import React, { useEffect, useState } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Controller } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import {
  IFormElementOptionHelperTextProp,
  IFormElementOptionProp,
  IFormElementProps,
} from 'models/form';
import { colors } from 'theme/colors';
import { Tooltip } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { getNestedErrorMessage } from './helpers';

interface IFormRadioButtonGroup extends IFormElementProps {
  label: string;
  options: IFormElementOptionProp[];
  onChangeOption?: (arg: EventTarget) => void;
  booleanType?: boolean;
  withValidation?: boolean;
  helperTooltipText?: IFormElementOptionHelperTextProp[];
  required?: boolean;
  oneLine?: boolean;
  selectedByDefault?: string;
}
export const FormRadioGroup = ({
  name,
  label,
  options,
  control,
  errors,
  disabled,
  booleanType = false,
  withValidation = false,
  helperTooltipText,
  onChangeOption,
  required = false,
  oneLine = false,
  selectedByDefault,
}: IFormRadioButtonGroup) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    let isError = false;
    const keys = name.split('.');
    const errMessage = getNestedErrorMessage(errors, keys);
    if (keys && errMessage) {
      isError = true;
      setErrorMessage(errMessage);
    }
    if (!isError) {
      setErrorMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  const checkedVal = (field: any, option: any): boolean | undefined => {
    if (booleanType) {
      if (field?.value === undefined || field?.value === null) return false;
      return field?.value.toString() === option.value.toString();
    }
    if (selectedByDefault && field.value === '')
      return option.value.toString() === selectedByDefault;
    return field?.value === option.value;
  };

  // eslint-disable-next-line consistent-return
  const getHelper = (value: string | number) => {
    const helper = helperTooltipText?.filter((item) => item.value === value);
    if (helper?.length) {
      return (
        <Tooltip
          title={helper[0].text}
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#082a4a',
                fontWeight: 400,
                padding: '5px 15px',
                fontSize: '0.75rem',
                lineHeight: '1rem',
                maxWidth: '500px',
              },
            },
          }}
        >
          <div style={{ marginRight: '24px', marginLeft: '-8px', paddingTop: '7px' }}>
            <Icon type={EIconTypes.info2} color={colors.primary100} fontSize={18} />
          </div>
        </Tooltip>
      );
    }
  };

  return (
    <StyledFormElementWrapper oneLine={oneLine}>
      <FormLabel name={name} label={required ? `${label}*` : label} />
      <RadioGroup
        aria-labelledby={name}
        name={name}
        sx={{ flexDirection: 'row', alignItems: 'center' }}
      >
        {options.map((option: IFormElementOptionProp) => (
          <>
            <Controller
              key={option.value.toString()}
              name={name}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  {...field}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: 14,
                      fontWeight: 400,
                      color: `${colors.black} !important`,
                    },
                  }}
                  disabled={disabled}
                  value={option.value.toString()}
                  checked={checkedVal(field, option)}
                  onChange={(e) => {
                    field.onChange(e);
                    if (onChangeOption) {
                      onChangeOption(e.target);
                    }
                  }}
                  control={
                    <Radio
                      size="small"
                      id={`${name}-${option.value}`}
                      sx={{
                        color: colors.gray400,
                        '&.Mui-checked': {
                          color: disabled ? colors.gray400 : colors.primary700,
                        },
                      }}
                    />
                  }
                  label={option.label}
                />
              )}
            />
            {helperTooltipText ? getHelper(option.value) : ''}
          </>
        ))}
      </RadioGroup>
      {(withValidation || errorMessage) && <FormErrorLabel label={errorMessage} />}
    </StyledFormElementWrapper>
  );
};
